import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { count, dateTime, money } from '@/lib/format';
import { useLoyaltyStore } from '@/stores/loyalty.store';
import { Choices } from '@/components/fields';
import { Section } from '@/components/page';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui';

/**
 * A customer's loyalty points (ADR-083): the total and what it would be worth
 * as store credit, every movement, and — for whoever may refund — a change by
 * hand with a reason kept on the ledger.
 */
export function LoyaltyPanel({ customerId, canAdjust }: { customerId: string; canAdjust: boolean }) {
  const { loyalty, entries, nextCursor, loading, error, load, loadMore } = useLoyaltyStore();
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    void load(customerId);
  }, [customerId, load]);

  // A store without the programme, and a customer without points: nothing to show.
  if (loyalty && !loyalty.enabled && loyalty.points === 0 && entries.length === 0) return null;

  return (
    <Section
      title={t('loyaltyPoints')}
      subtitle={loyalty ? t('loyaltyWorth', { amount: money(loyalty.value, locale, loyalty.currency) }) : undefined}
      action={
        canAdjust && !adjusting ? (
          <Button variant="secondary" size="sm" onClick={() => setAdjusting(true)}>
            {t('loyaltyAdjust')}
          </Button>
        ) : undefined
      }
      flush
    >
      <div className="border-t border-line px-5 py-4 text-sm">
        <p data-numeric className="text-2xl font-semibold text-ink">
          {loyalty ? count(loyalty.points, locale) : '—'}
        </p>
        {/* Their tier by a year's spend (ADR-115), and what reaches the next. */}
        {loyalty?.tier ? (
          <p className="mt-1 text-xs text-ink-muted">{t('loyaltyTierLine', { tier: loyalty.tier.name[locale], rate: String(loyalty.tier.earnPercent) })}</p>
        ) : null}
        {loyalty?.nextTier ? (
          <p className="mt-0.5 text-xs text-ink-muted">
            {t('loyaltyNextTier', { amount: money(loyalty.nextTier.remaining, locale, loyalty.currency), tier: loyalty.nextTier.name[locale] })}
          </p>
        ) : null}
        {error && !adjusting ? (
          <div className="mt-3">
            <Alert>{error}</Alert>
          </div>
        ) : null}
        {adjusting ? <AdjustForm customerId={customerId} onDone={() => setAdjusting(false)} /> : null}
      </div>
      {entries.length > 0 ? (
        <ul className="divide-y divide-line border-t border-line">
          {entries.map((entry) => (
            <li key={entry.id} className="flex items-start justify-between gap-3 px-5 py-2.5 text-sm">
              <span className="min-w-0">
                <span className="block text-ink">{t(`loyaltyKind_${entry.kind}` as MessageKey)}</span>
                <span className="block text-xs text-ink-muted">
                  {dateTime(entry.createdAt, locale)}
                  {entry.order ? (
                    <>
                      {' · '}
                      <Link to={`/orders/${entry.order.id}`} dir="ltr" className="text-brand-700 hover:underline">
                        {entry.order.number}
                      </Link>
                    </>
                  ) : null}
                </span>
                {entry.note && entry.kind === 'ADJUSTMENT' ? <span className="block text-xs text-ink-muted">{entry.note}</span> : null}
              </span>
              <span className="shrink-0 text-end">
                <span data-numeric dir="ltr" className={entry.points > 0 ? 'block font-medium text-success' : 'block font-medium text-ink'}>
                  {entry.points > 0 ? '+' : '−'}
                  {count(Math.abs(entry.points), locale)}
                </span>
                <span data-numeric className="block text-xs text-ink-muted">
                  {count(entry.pointsAfter, locale)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : !loading ? (
        <p className="border-t border-line px-5 py-3 text-sm text-ink-muted">{t('walletNoMovements')}</p>
      ) : null}
      {nextCursor ? (
        <div className="border-t border-line px-5 py-2.5">
          <Button variant="ghost" size="sm" disabled={loading} onClick={() => void loadMore()}>
            {t('loadMore')}
          </Button>
        </div>
      ) : null}
    </Section>
  );
}

function AdjustForm({ customerId, onDone }: { customerId: string; onDone: () => void }) {
  const { adjust, saving, error } = useLoyaltyStore();
  const t = useT();
  const [direction, setDirection] = useState<'add' | 'take'>('add');
  const [points, setPoints] = useState('');
  const [note, setNote] = useState('');
  const amount = Number(points);
  const ready = Number.isInteger(amount) && amount > 0 && note.trim() !== '';

  // A button, not a form: the panel sits beside the customer page's own forms.
  async function submit() {
    if (!ready) return;
    if (await adjust(customerId, direction === 'add' ? amount : -amount, note.trim())) onDone();
  }

  return (
    <div className="mt-4 flex flex-col gap-3">
      <Choices
        legend={t('loyaltyAdjust')}
        legendHidden
        name="loyalty-direction"
        columns={2}
        value={direction}
        onChange={setDirection}
        options={[
          { value: 'add', label: t('loyaltyAdd') },
          { value: 'take', label: t('loyaltyTake') },
        ]}
      />
      <Field label={t('loyaltyPointsLabel')} htmlFor="loyalty-points">
        <Input id="loyalty-points" dir="ltr" inputMode="numeric" autoComplete="off" value={points} onChange={(event) => setPoints(event.target.value.replace(/\D/g, ''))} />
      </Field>
      <Field label={t('refundWhy')} htmlFor="loyalty-note" hint={t('walletNoteHint')}>
        <Textarea id="loyalty-note" rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>
      {error ? <Alert>{error}</Alert> : null}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onDone} disabled={saving}>
          {t('cancel')}
        </Button>
        <Button size="sm" disabled={!ready || saving} onClick={() => void submit()}>
          {t('save')}
        </Button>
      </div>
    </div>
  );
}
